/**
 * A code that arrived from a printed label.
 *
 * The QR on every label holds `https://host/r/C-AHYD15`, which the server bounces
 * to `/?scan=C-AHYD15`. Read once at start-up and taken out of the address bar
 * straight away: leaving it there would re-open the same record on every reload,
 * and it would sit in the browser history of a shared workstation.
 *
 * Held outside React — and outside the router — because it has to survive the
 * login screen. Someone scanning a shelf on a phone that has been logged out
 * should land on that shelf after signing in, not on the default page with the
 * scan forgotten.
 */

let pending: string | null = null;
let pendingPrinterProfile: string | null = null;

/** The UUID in a calibration QR, whether it arrived as an app URL or camera read. */
export function printerProfileIdFromUrl(value: string): string | null {
  try {
    const id = new URL(value).searchParams.get('printer_profile');
    return id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
      ? id
      : null;
  } catch { return null; }
}

/**
 * Take the code out of the address and put the address where that code belongs.
 * Call once, before the router starts reading `window.location`.
 */
export function captureIncomingScan(): void {
  const found = new URLSearchParams(window.location.search).get('scan');
  if (!found) return;
  pending = found;
  window.history.replaceState(null, '', '/scan');
}

/** Capture a saved-printer profile opened from its calibration-label QR. */
export function captureIncomingPrinterProfile(): void {
  const found = new URLSearchParams(window.location.search).get('printer_profile');
  if (!found) return;
  pendingPrinterProfile = found;
  // Old snapshot QR codes keep opening during migration.  The management page
  // can turn one into a named, mutable profile without pretending it already
  // has the new lifecycle or template bindings.
  window.history.replaceState(null, '', `/print/printers?legacy_id=${encodeURIComponent(found)}`);
}

/**
 * A code that arrived while the application was already running.
 *
 * On Android a label's QR opens this application directly rather than a
 * browser, and the code comes in as an event rather than as an address. There
 * is no page load to capture it at, so it is handed over here instead.
 */
export function offerIncomingScan(code: string): void {
  pending = code;
}

export function offerIncomingPrinterProfile(profileId: string): void {
  pendingPrinterProfile = profileId;
}

/** The captured code, once. Reading it consumes it. */
export function takeIncomingScan(): string | null {
  const found = pending;
  pending = null;
  return found;
}

/** The captured printer-profile id, once. */
export function takeIncomingPrinterProfile(): string | null {
  const found = pendingPrinterProfile;
  pendingPrinterProfile = null;
  return found;
}
